import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useManualUpload } from "../../hooks/useManualUpload";

const cardStyle = {
  backgroundColor: "var(--bg-elevated)",
  borderRadius: "8px",
  padding: "24px",
  maxWidth: "560px",
  color: "var(--text-primary)",
};

const inputStyle = {
  backgroundColor: "var(--bg-elevated-2)",
  color: "var(--text-primary)",
};

const ManualUpload = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [publisherName, setPublisherName] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const { uploadState, uploadCall } = useManualUpload();

  const validate = useCallback(() => {
    const next = {};
    if (!phoneNumber.trim()) next.phoneNumber = "Phone number is required";
    if (!publisherName.trim()) next.publisherName = "Publisher name is required";
    if (!audioFile) next.audioFile = "An audio file is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [phoneNumber, publisherName, audioFile]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validate()) return;

      try {
        await uploadCall({ phoneNumber: phoneNumber.trim(), publisherName: publisherName.trim(), audioFile });
        // The server has already stored the recording and queued it - QC
        // processing (transcription, disposition, summary) runs in the
        // background from here on, independent of this page. Take the user
        // straight to the list where they can watch its status update live.
        toast.success(
          "Recording uploaded. QC processing is running in the background, you don't need to wait here."
        );
        navigate("/calls/manual");
      } catch (err) {
        toast.error(err.message || "Upload failed");
      }
    },
    [phoneNumber, publisherName, audioFile, uploadCall, validate, navigate]
  );

  return (
    <div className="mt-2">
      <div style={cardStyle}>
        <h5 className="mb-3">Manual Call Upload</h5>
        <p className="text-secondary" style={{ fontSize: "0.85rem" }}>
          Upload a call recording to run it through the same QC pipeline used
          for incoming calls (disposition, summary, and AI analysis).
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-3">
            <label className="form-label">Phone Number</label>
            <input
              type="text"
              className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
              style={inputStyle}
              placeholder="e.g. (555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={uploadState.isLoading}
            />
            {errors.phoneNumber && (
              <div className="invalid-feedback">{errors.phoneNumber}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">Publisher Name</label>
            <input
              type="text"
              className={`form-control ${errors.publisherName ? "is-invalid" : ""}`}
              style={inputStyle}
              placeholder="Publisher name"
              value={publisherName}
              onChange={(e) => setPublisherName(e.target.value)}
              disabled={uploadState.isLoading}
            />
            {errors.publisherName && (
              <div className="invalid-feedback">{errors.publisherName}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">Audio File</label>
            <input
              type="file"
              ref={fileInputRef}
              accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm,.aac,.flac"
              className={`form-control ${errors.audioFile ? "is-invalid" : ""}`}
              style={inputStyle}
              onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
              disabled={uploadState.isLoading}
            />
            {errors.audioFile && (
              <div className="invalid-feedback">{errors.audioFile}</div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={uploadState.isLoading}
          >
            {uploadState.isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-1" />
                Upload & Process
              </>
            )}
          </button>

          {uploadState.isLoading && (
            <p className="text-secondary mt-2 mb-0" style={{ fontSize: "0.8rem" }}>
              Sending the file now. For longer recordings this can take a
              minute. Once it's sent, QC processing continues in the
              background and you're free to navigate elsewhere.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ManualUpload;
